import { z } from "zod";
import { PartialRuleSchema, RuleSchema, FeatureSchema } from "../../types";

export const AddFeatureOperationSchema = z
  .object({
    kind: z.literal("add_feature"),
    data: FeatureSchema.safeExtend({
      name: z.string().min(1),
    }),
  })
  .brand("AddFeatureOperation");
export type AddFeatureOperation = z.infer<typeof AddFeatureOperationSchema>;

export function makeAddFeatureOperation(data: z.input<typeof AddFeatureOperationSchema>["data"]): AddFeatureOperation {
  return AddFeatureOperationSchema.parse({
    kind: "add_feature",
    data,
  });
}

export const ModifyFeatureOperationSchema = z
  .object({
    kind: z.literal("modify_feature"),
    data: z.object({
      name: z.string().min(1),
      default_state: z.string().min(1).optional(),
      allowed_states: z.array(z.string().min(1)).min(1).optional(),
    }),
  })
  .brand("ModifyFeatureOperation");
export type ModifyFeatureOperation = z.infer<typeof ModifyFeatureOperationSchema>;

export function makeModifyFeatureOperation(
  data: z.input<typeof ModifyFeatureOperationSchema>["data"],
): ModifyFeatureOperation {
  return ModifyFeatureOperationSchema.parse({
    kind: "modify_feature",
    data,
  });
}

export const RemoveFeatureOperationSchema = z
  .object({
    kind: z.literal("remove_feature"),
    data: z.object({
      name: z.string().min(1),
    }),
  })
  .brand("RemoveFeatureOperation");
export type RemoveFeatureOperation = z.infer<typeof RemoveFeatureOperationSchema>;

export function makeRemoveFeatureOperation(
  data: z.input<typeof RemoveFeatureOperationSchema>["data"],
): RemoveFeatureOperation {
  return RemoveFeatureOperationSchema.parse({
    kind: "remove_feature",
    data,
  });
}

export const AddRuleOperationSchema = z
  .object({
    kind: z.literal("add_rule"),
    data: RuleSchema,
  })
  .brand("AddRuleOperation");
export type AddRuleOperation = z.infer<typeof AddRuleOperationSchema>;

export function makeAddRuleOperation(data: z.input<typeof AddRuleOperationSchema>["data"]): AddRuleOperation {
  return AddRuleOperationSchema.parse({
    kind: "add_rule",
    data,
  });
}

export const ModifyRuleOperationSchema = z
  .object({
    kind: z.literal("modify_rule"),
    data: PartialRuleSchema,
  })
  .brand("ModifyRuleOperation");
export type ModifyRuleOperation = z.infer<typeof ModifyRuleOperationSchema>;

export function makeModifyRuleOperation(data: z.input<typeof ModifyRuleOperationSchema>["data"]): ModifyRuleOperation {
  return ModifyRuleOperationSchema.parse({
    kind: "modify_rule",
    data,
  });
}

export const RemoveRuleOperationSchema = z
  .object({
    kind: z.literal("remove_rule"),
    data: z.object({
      id: z.string().min(1),
    }),
  })
  .brand("RemoveRuleOperation");
export type RemoveRuleOperation = z.infer<typeof RemoveRuleOperationSchema>;

export function makeRemoveRuleOperation(data: z.input<typeof RemoveRuleOperationSchema>["data"]): RemoveRuleOperation {
  return RemoveRuleOperationSchema.parse({
    kind: "remove_rule",
    data,
  });
}

export const ReorderRulesOperationSchema = z
  .object({
    kind: z.literal("reorder_rules"),
    data: z.object({
      rule_ids: z.array(z.string().min(1)).min(1),
    }),
  })
  .brand("ReorderRulesOperation");
export type ReorderRulesOperation = z.infer<typeof ReorderRulesOperationSchema>;

export function makeReorderRulesOperation(
  data: z.input<typeof ReorderRulesOperationSchema>["data"],
): ReorderRulesOperation {
  return ReorderRulesOperationSchema.parse({
    kind: "reorder_rules",
    data,
  });
}

export type Operation =
  | AddFeatureOperation
  | ModifyFeatureOperation
  | RemoveFeatureOperation
  | AddRuleOperation
  | ModifyRuleOperation
  | RemoveRuleOperation
  | ReorderRulesOperation;

export const OperationSchema = z.discriminatedUnion("kind", [
  AddFeatureOperationSchema,
  ModifyFeatureOperationSchema,
  RemoveFeatureOperationSchema,
  AddRuleOperationSchema,
  ModifyRuleOperationSchema,
  RemoveRuleOperationSchema,
  ReorderRulesOperationSchema,
]);
